import React from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import {
  CodeMirror,
  CodeMirrorRefType,
} from "@/components/editor/code/code-mirror";
import { Note } from "@/providers/note-provider";
import { setData, getData, deleteData } from "@/lib/indexed-db";
import { showMessageBox } from "@/components/blocks/message-box";
import { dateTimeFormat, timestampToDate } from "@/lib/date-tools";

interface NoteContentProps {
  note: Note;
  onClose: () => void;
}

export const NoteContent = ({ note, onClose }: NoteContentProps) => {
  const editorRef = React.useRef<CodeMirrorRefType>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const [title, setTitle] = React.useState(note.title);
  const [description, setDescription] = React.useState(note.description);
  const [content, setContent] = React.useState<string>(note.content);
  const [draft, setDraft] = React.useState<any | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const isChanged = React.useMemo(() => {
    const target = {
      title: draft?.title ?? note.title,
      description: draft?.description ?? note.description,
      content: draft?.content ?? note.content,
    };
    return (
      title !== target.title ||
      description !== target.description ||
      content !== target.content
    );
  }, [note, draft, title, description, content]);

  const onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const onChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const LoadFromDraft = async () => {
    const localSaved = await getData("notes", note._id);
    console.log("[NoteContent] LoadFromDraft", localSaved);
    if (localSaved) {
      const { data } = localSaved;
      const noteDate = new Date(timestampToDate(note.updatedAt)).getTime();
      const draftData = new Date(data.updatedAt).getTime();
      // console.log("[NoteContent] checkDraftExists", noteDate, draftData);

      // 서버의 데이터보다 더 오래된 임시 저장 데이터가 있는 경우.
      if (noteDate > draftData) {
        const choice = await showMessageBox({
          type: "Confirm_Alert",
          title: "서버의 데이터보다 더 오래된 임시 저장 데이터가 있습니다.",
          description: `서버 데이터: ${dateTimeFormat(note.updatedAt)}\n임시 데이터: ${dateTimeFormat(data.updatedAt)}\n임시 저장 데이터를 불러오시겠습니까?`,
        });
        // 사용자가 "아니오"를 선택한 경우, 임시 저장 데이터를 삭제합니다.
        if (choice === "No") {
          deleteData("notes", note._id);
          console.log("[NoteContent] 임시 저장 데이터를 삭제했습니다.");
          return;
        }
      }

      // 임시 저장 데이터를 불러옵니다.
      setTitle(data.title);
      setDescription(data.description);
      // setContent(data.content);
      editorRef.current?.setContent(data.content);
      console.log("[NoteContent] 임시 저장 데이터를 불러왔습니다.");
      setDraft(data);
    }
  };

  const SaveToDraft = React.useCallback(
    async (
      currentTitle: string,
      currentDesc: string,
      currentContent: string,
    ) => {
      if (!note || isSaving) return;

      try {
        setIsSaving(true);
        const data = {
          _id: note._id,
          parent: note.parent,
          title:
            currentTitle.trim() === ""
              ? dateTimeFormat(Date.now().toString())
              : currentTitle,
          description: currentDesc,
          content: currentContent,
          updatedAt: new Date().toISOString(),
        };
        await setData("notes", note._id, data);
        setDraft(data);
        console.log("[NoteContent] 노트 자동 저장 완료", data);
      } catch (error) {
        console.error("[NoteContent]", error);
      } finally {
        setIsSaving(false);
      }
    },
    [note, isSaving],
  );

  // 노트 오픈.
  React.useEffect(() => {
    // 임시 저장 데이터가 있는지 확인.
    LoadFromDraft();
  }, []);

  // 에디터 폴링 및 디바운스 로직
  React.useEffect(() => {
    // 1초마다 에디터의 최신 내용을 가져와서 React 상태와 비교
    const intervalId = setInterval(() => {
      if (!editorRef.current) return;

      const latestEditorContent = editorRef.current.getContent();
      // 내용이 변경되었을 때만 setContent를 호출하여 리렌더링 유발
      if (latestEditorContent !== content) {
        setContent(latestEditorContent);
      }
    }, 1000); // 폴링 주기는 1초면 충분합니다.

    return () => clearInterval(intervalId);
  }, [content]);

  // 상태(content, title, description) 변화 감지 후 2초 뒤 저장
  React.useEffect(() => {
    if (!isChanged) return;

    const timeoutId = setTimeout(() => {
      SaveToDraft(title, description, content);
    }, 2000); // 사용자가 입력을 멈추고 2초 뒤 저장

    return () => clearTimeout(timeoutId);
  }, [isChanged, title, description, content, SaveToDraft]);

  React.useEffect(() => {
    // if (textareaRef.current) {
    //   textareaRef.current.style.height = "auto";
    //   textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    // }
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const maxHeight = 120;
      // 초기화.
      textarea.style.height = "24px";
      textarea.style.maxHeight = `${maxHeight}px`;
      // 높이 계산.
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [description]);

  // 컴포넌트 언마운트 또는 닫기 버튼 클릭 시 즉시 저장
  const onCloseHandler = React.useCallback(async () => {
    const finalContent = editorRef.current?.getContent() || content;
    if (isChanged) {
      console.log("[NoteContent] 변경사항이 있어 종료 전 저장합니다.");
      await SaveToDraft(title, description, finalContent);
    }
    onClose();
  }, [isChanged, title, description, content, SaveToDraft, onClose]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // 배경 영역이 클릭된 경우에만 닫기
    if (e.target === e.currentTarget) {
      onCloseHandler();
    }
  };

  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseHandler();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onCloseHandler]);

  return (
    <div
      className="absolute inset-0 w-full h-full bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-2"
      onClick={handleBackgroundClick}
    >
      <div className="bg-card w-full max-w-[80vw] h-full max-h-[60vh] rounded-lg shadow-lg flex flex-col border-2">
        <div className="flex justify-between items-center p-4 border-b relative">
          <div className="flex flex-col flex-1 justify-between items-center">
            <input
              value={title}
              onChange={onChangeTitle}
              className="w-full bg-transparent mb-2 p-1 text-lg font-semibold border-b border-dashed border-muted-foreground/30 focus:outline-none"
              placeholder="Note Title"
            />
            <textarea
              ref={textareaRef}
              value={description}
              onChange={onChangeDescription}
              className="w-full p-1 h-6 min-h-[24px] resize-none bg-transparent border-none focus:outline-none"
              placeholder="Note Description"
            />
          </div>
          <div className="flex justify-between items-center absolute right-0 top-0">
            {/* <Button
              size="icon"
              variant="ghost"
              onClick={onSaveHandler}
              disabled={!canSave || isSaving || !needSave}
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
            </Button> */}
            <Button
              size="icon"
              variant="ghost"
              onClick={onCloseHandler}
              //disabled={isSaving}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="relative flex-1 overflow-hidden">
          <CodeMirror initialCode={note.content} ref={editorRef} className="" />
        </div>
      </div>
    </div>
  );
};
